import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { LegalEntityForSelectFragment, ProjectForSelectFragment } from 'generated/resources/graphql'
import { ExpensesValueObjectsCreationTypeEnum } from 'generated/resources/graphql'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

interface ExpensesFilters {
  employees: EmployeeForSelectFragmentNoAccess[]
  legalEntities: LegalEntityForSelectFragment[]
  creationType: ExpensesValueObjectsCreationTypeEnum | undefined
  currency: string
  hasSetCurrency: boolean
  projects: ProjectForSelectFragment[]
}

export const usePersistedFilters = () => {
  const defaultFilters: ExpensesFilters = {
    employees: [],
    legalEntities: [],
    creationType: undefined,
    currency: 'EUR',
    hasSetCurrency: false,
    projects: [],
  }
  const [filters, setFilters] = useLocalStorage<ExpensesFilters>(
    LOCAL_STORAGE_KEYS.expensesReportsFilters,
    defaultFilters
  )
  const orDefault = (_filters: ExpensesFilters | null) => {
    if (!_filters) return defaultFilters

    // Ensure backward compatibility for new fields
    return {
      ...defaultFilters,
      ..._filters,
      // Ensure projects is always an array (for backward compatibility)
      projects: _filters.projects ?? [],
    }
  }

  const setCurrencyFromString = (newCurrency: string) => {
    setFilters((prev) => {
      return {
        ...orDefault(prev),
        currency: newCurrency,
        hasSetCurrency: true,
      }
    })
  }

  const setInitialCurrency = (newCurrency: string) => {
    if (!filters?.hasSetCurrency) {
      setCurrencyFromString(newCurrency)
    }
  }

  const setFilteredEmployees = (employees: EmployeeForSelectFragmentNoAccess[]) => {
    setFilters((prev) => {
      return { ...orDefault(prev), employees }
    })
  }

  const setFilteredLegalEntities = (legalEntities: LegalEntityForSelectFragment[]) => {
    setFilters((prev) => {
      return { ...orDefault(prev), legalEntities }
    })
  }

  const setFilteredCreationType = (
    creationType: ExpensesValueObjectsCreationTypeEnum | undefined
  ) => {
    setFilters((prev) => {
      return { ...orDefault(prev), creationType }
    })
  }

  const setFilteredProjects = (projects: ProjectForSelectFragment[]) => {
    setFilters((prev) => {
      return { ...orDefault(prev), projects }
    })
  }

  return {
    filters: orDefault(filters),
    setFilteredEmployees,
    setFilteredLegalEntities,
    setFilteredCreationType,
    setFilteredCurrency: setCurrencyFromString,
    setInitialCurrency,
    setFilteredProjects,
  }
}
